import { useEffect, useState, useRef } from "react";
import {
  Table,
  TableHeader,
  TableColumn,
  TableBody,
  TableRow,
  TableCell,
  Chip,
  Spinner,
  Input,
  Button,
  Dropdown,
  DropdownTrigger,
  DropdownMenu,
  DropdownSection,
  DropdownItem,
  Tooltip,
} from "@heroui/react";
import {
  Building,
  Eye,
  MagnifyingGlass,
  ArrowsDownUp,
  SortAscending,
  SortDescending,
  House,
  SkipForward,
  Flag,
  Hammer,
  PiggyBank,
  Image,
  FadersHorizontal,
  Buildings,
  CaretUp,
  CaretDown,
  FileArrowDown,
  X,
  Bed,
  TreePalm,
  CalendarBlank,
} from "@phosphor-icons/react";
import * as Sentry from "@sentry/react";
import { toast } from "react-toastify";
import { UnitDTO, SaleStatus } from "shared/types/unit";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { API_URL } from "constants/index";
import { useSession } from "@clerk/clerk-react";
import { useNavigate } from "react-router-dom";
import { EditableNumberCell } from "shared/components/table/EditableNumberCell";
import { ContractPhase, ContractProduct } from "shared/types/rental-contracts";
import { applyMask } from "shared/utils/masks";
import { ImagesContainer } from "shared/components/imagesContainer/imagesContainer";
import { FileLabel } from "shared/types/file";
import { AcquisitionPhase } from "shared/types/acquisition";
import { Lightning } from "@phosphor-icons/react";
import {
  getEPCColor,
  getDisplayLabel,
  EPCLabel,
} from "shared/components/maintenance/hooks/useEnergyPerformanceData";

// Labels that mean "no real EPC on file" — rendered as a neutral placeholder.
const EPC_EMPTY_LABELS: string[] = [
  EPCLabel.UNKNOWN,
  EPCLabel.ONTBREKEND,
  EPCLabel.NIET_VAN_TOEPASSING,
];

/**
 * Renders the current EPC for a unit as a colored label chip (same color
 * scheme as the energieprestaties overview), with the kWh underneath.
 */
const renderUnitEpc = (unit: UnitDTO) => {
  const epc = unit.currentEpc;
  if (!epc || !epc.label || EPC_EMPTY_LABELS.includes(epc.label)) {
    return <span className="text-neutral-secondary-mid text-sm">-</span>;
  }

  const label = epc.label as EPCLabel;
  return (
    <div className="flex flex-col items-start gap-0.5">
      <span
        className={`rounded-lg px-2 py-0.5 text-sm font-bold ${getEPCColor(label)}`}
      >
        {getDisplayLabel(label)}
      </span>
      {epc.kWh && (
        <span className="text-neutral-secondary-mid text-xs">
          {epc.kWh} kWh/m²
        </span>
      )}
    </div>
  );
};

/**
 * Formats the `contractStartDate` the backend sends ("YYYY-MM-DD") as dd/mm/yyyy.
 *
 * Formatted from the string parts rather than through `new Date(...)` on
 * purpose: a date-only string is parsed as UTC midnight, which a browser in a
 * timezone behind UTC then renders as the previous day. A contract start date
 * is a calendar date, so it must not shift with the reader's timezone.
 */
const formatContractStartDate = (isoDate: string): string | null => {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(isoDate);
  if (!match) return null;
  const [, year, month, day] = match;
  return `${day}/${month}/${year}`;
};

const Units = () => {
  const { session } = useSession();
  const navigate = useNavigate();
  const [units, setUnits] = useState<UnitDTO[]>([]);
  const [filteredUnits, setFilteredUnits] = useState<UnitDTO[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortConfig, setSortConfig] = useState({
    key: "id",
    direction: "ascending",
  });
  const [filters, setFilters] = useState({
    saleStatus: [] as SaleStatus[],
    rentalStatus: [] as string[],
    renovation: [] as string[],
    includeDueDiligence: false,
  });
  const [visibleUnits, setVisibleUnits] = useState<Set<string>>(new Set());
  const observerRef = useRef<IntersectionObserver | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  // Fetch all units with relations needed for the table
  const fetchUnits = async () => {
    try {
      setIsLoading(true);
      const response = await fetchWithAuth(
        `${API_URL}/units/dynamic`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            relations: [
              "building",
              "building.acquisitionFile",
              "building.company",
              "contracts",
              "contracts.products",
              "contracts.products.price",
              "contracts.products.template",
              // Needed for the "Slaapkamers" column (numBedrooms lives here)
              "unitInspectionInfo",
            ],
            // Enrich each unit with its current (most recent) EPC for the EPC column
            includeCurrentEpc: true,
            // Enrich each unit with the start date of the contract behind its
            // rental status ("Startdatum huurcontract" column). Done backend-side
            // so the milestones don't have to be joined into this already heavy query.
            includeContractStartDate: true,
          }),
        },
        session,
      );
      const data = await response.json();
      const allUnits = Array.isArray(data) ? data : [];
      setUnits(allUnits);
      applyFilters(allUnits);
    } catch (error) {
      console.error("Error fetching units:", error);
      Sentry.captureException(error, {
        tags: { screen: "units-list", operation: "fetchUnits" },
      });
      toast.error("Units konden niet worden geladen");
      setUnits([]);
      setFilteredUnits([]);
    } finally {
      setIsLoading(false);
    }
  };

  const applyFilters = (unitsList: UnitDTO[]) => {
    if (!Array.isArray(unitsList)) {
      setFilteredUnits([]);
      return;
    }

    let result = [...unitsList];

    // Apply building ownership/due diligence filter
    result = result.filter((unit) => {
      if (!unit.building) return false;

      if (filters.includeDueDiligence) {
        return (
          unit.building.owner ||
          (unit.building.acquisitionFile &&
            unit.building.acquisitionFile.acquisitionPhase ===
              AcquisitionPhase.DEAL)
        );
      } else {
        return unit.building.owner;
      }
    });

    // Apply search filter
    if (searchTerm) {
      result = result.filter((unit) =>
        unit.id?.toLowerCase().includes(searchTerm.toLowerCase()),
      );
    }

    // Apply status filters
    if (filters.saleStatus.length > 0) {
      result = result.filter((unit) =>
        filters.saleStatus.includes(unit.saleStatus as SaleStatus),
      );
    }

    if (filters.rentalStatus.length > 0) {
      result = result.filter((unit) => {
        const status = getRentalStatus(unit);
        return filters.rentalStatus.includes(status);
      });
    }

    if (filters.renovation.length > 0) {
      result = result.filter((unit) => {
        const renovationStatus = unit.vacantDueToRenovation
          ? "In Renovation"
          : "No Renovation";
        return filters.renovation.includes(renovationStatus);
      });
    }

    // Apply sorting
    result.sort((a, b) => {
      let aValue, bValue;

      switch (sortConfig.key) {
        case "id":
          aValue = a.id || "";
          bValue = b.id || "";
          break;
        case "surface":
          aValue = a.netSurface || 0;
          bValue = b.netSurface || 0;
          break;
        case "outsideSpaceSurface":
          aValue = a.outsideSpaceSurface || 0;
          bValue = b.outsideSpaceSurface || 0;
          break;
        case "price":
          aValue = a.estimatedRentalValue || 0;
          bValue = b.estimatedRentalValue || 0;
          break;
        case "actualRentalPrice":
          aValue = getActualRentalPrice(a) || 0;
          bValue = getActualRentalPrice(b) || 0;
          break;
        case "parkingPrice":
          aValue = getParkingRentalPrice(a) || 0;
          bValue = getParkingRentalPrice(b) || 0;
          break;
        case "contractStartDate":
          // "YYYY-MM-DD" sorts correctly as a plain string; units without a
          // start date get "" and group together at one end.
          aValue = a.contractStartDate || "";
          bValue = b.contractStartDate || "";
          break;
        default:
          aValue = a.id || "";
          bValue = b.id || "";
      }

      if (aValue < bValue) return sortConfig.direction === "ascending" ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === "ascending" ? 1 : -1;
      return 0;
    });

    setFilteredUnits(result);
  };

  useEffect(() => {
    fetchUnits();
  }, []); // Only fetch once on component mount

  // Apply filters when units, searchTerm, or filters change
  useEffect(() => {
    applyFilters(units);
  }, [units, searchTerm, sortConfig, filters]);

  // Handle sorting
  const requestSort = (key: string) => {
    let direction = "ascending";
    if (sortConfig.key === key && sortConfig.direction === "ascending") {
      direction = "descending";
    }
    setSortConfig({ key, direction });
  };

  // Get the actual rental price from active contracts (excluding parking)
  const getActualRentalPrice = (unit: UnitDTO): number | null => {
    try {
      if (!unit.contracts || unit.contracts.length === 0) return null;

      // Find active contract
      const activeContract = unit.contracts.find(
        (contract) =>
          contract.phase === ContractPhase.ACTIVE ||
          contract.phase === ContractPhase.TERMINATION_PHASE,
      );

      if (!activeContract) return null;

      // If products are available, sum rental income products by ledger account
      if (activeContract.products && activeContract.products.length > 0) {
        let totalRentalPrice = 0;

        activeContract.products.forEach((product: ContractProduct) => {
          try {
            // Check if product has a rental income ledger account (excluding parking - 700003)
            // These are the main rental income accounts, excluding provisions (700020+, 700101+)
            const ledgerCode =
              product.template?.ledger?.toString() ||
              product.ledger?.toString() ||
              "";
            const isRentalIncome =
              ledgerCode === "700001" || // Huuropbrengsten Residentieel
              ledgerCode === "700002" || // Huuropbrengsten Commercieel
              ledgerCode === "700004" || // Huuropbrengsten Korte termijnverhuur
              ledgerCode === "700005" || // Opbrengsten Energie
              ledgerCode === "700006"; // Opbrengsten Meubilair

            if (isRentalIncome && product.price && product.price.length > 0) {
              // Match the rental tab / backend extractActivePrice logic:
              // only prices whose effectiveDate is today or earlier are active.
              // Picking the most recent regardless of effectiveDate causes the
              // "Initieel prijsniveau" to win over the latest "Verhoging huurprijs".
              const today = new Date();
              today.setHours(0, 0, 0, 0);

              const effectivePrices = product.price.filter((price) => {
                if (!price.effectiveDate) return true;
                const effectiveDate = new Date(price.effectiveDate);
                effectiveDate.setHours(0, 0, 0, 0);
                return effectiveDate <= today;
              });

              if (effectivePrices.length > 0) {
                const sortedPrices = [...effectivePrices].sort((a, b) => {
                  if (a.effectiveDate && b.effectiveDate) {
                    const dateDiff =
                      new Date(b.effectiveDate).getTime() -
                      new Date(a.effectiveDate).getTime();
                    if (dateDiff !== 0) return dateDiff;
                  }
                  return b.id - a.id;
                });

                const currentPrice = sortedPrices[0];
                if (currentPrice && currentPrice.amount) {
                  totalRentalPrice += currentPrice.amount;
                }
              }
            }
          } catch (productError) {
            console.error(
              `Error processing product for unit ${unit.id}:`,
              productError,
            );
            // Continue processing other products
          }
        });

        return totalRentalPrice > 0 ? totalRentalPrice : null;
      }

      // Fallback to activePrice if products not available
      return activeContract.activePrice?.price || null;
    } catch (error) {
      console.error(
        `Error calculating actual rental price for unit ${unit.id}:`,
        error,
      );
      return null;
    }
  };

  // Get the parking rental price from active contracts
  const getParkingRentalPrice = (unit: UnitDTO): number | null => {
    try {
      if (!unit.contracts || unit.contracts.length === 0) return null;

      // Find active contract
      const activeContract = unit.contracts.find(
        (contract) =>
          contract.phase === ContractPhase.ACTIVE ||
          contract.phase === ContractPhase.TERMINATION_PHASE,
      );

      if (!activeContract) return null;

      // If products are available, sum parking rental income products
      if (activeContract.products && activeContract.products.length > 0) {
        let totalParkingPrice = 0;
        let hasParkingProduct = false; // Track if any parking products exist

        activeContract.products.forEach((product: ContractProduct) => {
          try {
            // Check if product has parking rental income ledger account (700003)
            const ledgerCode =
              product.template?.ledger?.toString() ||
              product.ledger?.toString() ||
              "";
            const isParkingIncome = ledgerCode === "700003"; // Huuropbrengsten Parkings

            if (isParkingIncome) {
              hasParkingProduct = true; // Found a parking product

              if (product.price && product.price.length > 0) {
                // Only prices whose effectiveDate is today or earlier are active.
                const today = new Date();
                today.setHours(0, 0, 0, 0);

                const effectivePrices = product.price.filter((price) => {
                  if (!price.effectiveDate) return true;
                  const effectiveDate = new Date(price.effectiveDate);
                  effectiveDate.setHours(0, 0, 0, 0);
                  return effectiveDate <= today;
                });

                if (effectivePrices.length > 0) {
                  const sortedPrices = [...effectivePrices].sort((a, b) => {
                    if (a.effectiveDate && b.effectiveDate) {
                      const dateDiff =
                        new Date(b.effectiveDate).getTime() -
                        new Date(a.effectiveDate).getTime();
                      if (dateDiff !== 0) return dateDiff;
                    }
                    return b.id - a.id;
                  });

                  const currentPrice = sortedPrices[0];
                  if (
                    currentPrice &&
                    currentPrice.amount !== undefined &&
                    currentPrice.amount !== null
                  ) {
                    totalParkingPrice += currentPrice.amount;
                  }
                }
              }
            }
          } catch (productError) {
            console.error(
              `Error processing parking product for unit ${unit.id}:`,
              productError,
            );
            // Continue processing other products
          }
        });

        // Return 0 if parking products exist (even if price is 0), null if no parking products
        return hasParkingProduct ? totalParkingPrice : null;
      }

      return null;
    } catch (error) {
      console.error(
        `Error calculating parking rental price for unit ${unit.id}:`,
        error,
      );
      return null;
    }
  };

  // Get active provisions from active contract
  const getActiveProvisions = (unit: UnitDTO): number | null => {
    if (!unit.contracts || unit.contracts.length === 0) return null;

    // Find active contract
    const activeContract = unit.contracts.find(
      (contract) =>
        contract.phase === ContractPhase.ACTIVE ||
        contract.phase === ContractPhase.TERMINATION_PHASE,
    );

    if (!activeContract) return null;

    // Check if activeProvision is an object or a number
    if (activeContract.activeProvision) {
      if (
        typeof activeContract.activeProvision === "object" &&
        "price" in activeContract.activeProvision
      ) {
        return activeContract.activeProvision.price || null;
      }
      if (typeof activeContract.activeProvision === "number") {
        return activeContract.activeProvision;
      }
    }

    return null;
  };

  // Get rental status (Rented, Vacant, Soon vacant, etc.)
  const getRentalStatus = (unit: UnitDTO): string => {
    // First check if unit is in renovation
    if (unit.vacantDueToRenovation) {
      return "In renovatie";
    }

    if (!unit.contracts || unit.contracts.length === 0) {
      return "Leegstand";
    }

    // Check if unit has active contract
    const activeContract = unit.contracts.find(
      (contract) => contract.phase === ContractPhase.ACTIVE,
    );
    if (activeContract) {
      return "Verhuurd";
    }

    // Check if unit has termination phase contract
    const terminationContract = unit.contracts.find(
      (contract) => contract.phase === ContractPhase.TERMINATION_PHASE,
    );
    if (terminationContract) {
      return "Aankomende leegstand";
    }

    // Check for precontractual phase
    const precontractualContract = unit.contracts.find(
      (contract) => contract.phase === ContractPhase.CONCEPT,
    );
    if (precontractualContract) {
      return "Bijna verhuurde units";
    }

    return "Leegstand";
  };

  // Calculate price difference percentage
  const calculatePriceDifference = (unit: UnitDTO): number | null => {
    const actualPrice = getActualRentalPrice(unit);
    const estimatedPrice = unit.estimatedRentalValue;

    if (!actualPrice || !estimatedPrice) return null;

    return ((actualPrice - estimatedPrice) / estimatedPrice) * 100;
  };

  // Get the rental status chip color
  const getStatusChipColor = (status: string): any => {
    switch (status) {
      case "Leegstand":
        return "danger";
      case "Verhuurd":
        return "success";
      case "Aankomende leegstand":
        return "warning";
      case "Bijna verhuurde units":
        return "primary";
      case "In renovatie":
        return "warning";
      default:
        return "default";
    }
  };

  // Navigate to unit details
  const handleUnitClick = (unitId: string) => {
    navigate(`/unit/detail/${unitId}`);
  };

  /**
   * Persists the outside space surface (m² terras / tuin / buitenruimte) for a
   * single unit straight from the list and patches the value into local state
   * so the row — and a subsequent Excel export — reflect it without refetching
   * the whole list.
   *
   * Throws on failure so the calling cell can restore its previous value and
   * report the error to Sentry with the unit id attached.
   */
  const saveOutsideSpaceSurface = async (
    unitId: string,
    value: number | null,
  ) => {
    const response = await fetchWithAuth(
      `${API_URL}/units/update/${unitId}`,
      {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ outsideSpaceSurface: value }),
      },
      session,
    );

    if (!response.ok) {
      const details = await response.text().catch(() => "");
      throw new Error(
        `Opslaan oppervlakte buitenruimte mislukt voor unit ${unitId} (${response.status}): ${details}`,
      );
    }

    setUnits((previous) =>
      previous.map((unit) =>
        unit.id === unitId
          ? { ...unit, outsideSpaceSurface: value ?? undefined }
          : unit,
      ),
    );
    toast.success("Oppervlakte buitenruimte opgeslagen");
  };

  // Modified toggle functions to prevent dropdown closing
  const toggleDueDiligenceFilter = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    setFilters((prev) => ({
      ...prev,
      includeDueDiligence: !prev.includeDueDiligence,
    }));
  };

  // Toggle filter function for multi-select filters
  const toggleFilter = (
    filterType: string,
    value: any,
    e?: React.MouseEvent,
  ) => {
    e?.stopPropagation();
    setFilters((prev) => {
      const currentValues = [...(prev as any)[filterType]];
      const index = currentValues.indexOf(value);

      if (index === -1) {
        return { ...prev, [filterType]: [...currentValues, value] };
      } else {
        currentValues.splice(index, 1);
        return { ...prev, [filterType]: currentValues };
      }
    });
  };

  // Render the sorting icon
  const renderSortIcon = (key: string) => {
    if (sortConfig.key !== key) return <ArrowsDownUp size={16} />;
    return sortConfig.direction === "ascending" ? (
      <SortAscending size={16} />
    ) : (
      <SortDescending size={16} />
    );
  };

  // Export to Excel function
  const exportToExcel = async () => {
    try {
      // Send only unit IDs - backend will fetch and process
      const response = await fetchWithAuth(
        `${API_URL}/excel/export-units`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            unitIds: filteredUnits.map((unit) => unit.id),
          }),
        },
        session,
      );

      if (response.ok) {
        // Convert the response to a blob
        const blob = await response.blob();
        // Create a URL for the blob
        const url = window.URL.createObjectURL(blob);
        // Create a temporary anchor element
        const a = document.createElement("a");
        a.href = url;
        a.download = `units-export-${new Date().toISOString().split("T")[0]}.xlsx`;
        // Append the anchor to the body
        document.body.appendChild(a);
        // Trigger a click on the anchor
        a.click();
        // Remove the anchor from the body
        document.body.removeChild(a);
        // Release the object URL
        window.URL.revokeObjectURL(url);
      } else {
        throw new Error(
          `Export van units mislukt (${response.status} ${response.statusText})`,
        );
      }
    } catch (error) {
      console.error("Error exporting to Excel:", error);
      Sentry.captureException(error, {
        tags: { screen: "units-list", operation: "exportToExcel" },
        extra: { unitCount: filteredUnits.length },
      });
      toast.error("Export naar Excel mislukt");
    }
  };

  // Initialize visible units
  useEffect(() => {
    // Set up the initial preloaded units (first 10)
    if (Array.isArray(filteredUnits) && filteredUnits.length > 0) {
      const preloadCount = Math.min(filteredUnits.length, 10);
      const preloadedIds = filteredUnits
        .slice(0, preloadCount)
        .map((unit) => unit.id as string);

      setVisibleUnits(new Set(preloadedIds));
    }

    // Set up intersection observer for lazy loading the rest
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const unitId = entry.target.getAttribute("data-unit-id");
            if (unitId) {
              setVisibleUnits((prev) => new Set([...prev, unitId]));
            }
          }
        });
      },
      { rootMargin: "300px 0px" },
    );

    observerRef.current = observer;

    // Need a small delay to ensure DOM elements are ready
    setTimeout(() => {
      if (containerRef.current) {
        containerRef.current
          .querySelectorAll("[data-unit-id]")
          .forEach((el) => {
            observer.observe(el);
          });
      }
    }, 100);

    return () => {
      observer.disconnect();
    };
  }, [filteredUnits]);

  // Get filter labels for display
  const getActiveFilters = () => {
    const activeFilters = [];

    // Add sale status filters
    filters.saleStatus.forEach((status) => {
      activeFilters.push({
        type: "saleStatus",
        value: status,
        label: `Verkoop: ${status}`,
      });
    });

    // Add rental status filters
    filters.rentalStatus.forEach((status) => {
      activeFilters.push({
        type: "rentalStatus",
        value: status,
        label: `Verhuur: ${status}`,
      });
    });

    // Add renovation filters
    filters.renovation.forEach((status) => {
      activeFilters.push({
        type: "renovation",
        value: status,
        label: `Renovatie: ${status}`,
      });
    });

    // Add due diligence filter
    if (filters.includeDueDiligence) {
      activeFilters.push({
        type: "includeDueDiligence",
        value: true,
        label: "Inclusief panden in Due Diligence",
      });
    }

    return activeFilters;
  };

  // Remove a single filter
  const removeFilter = (type: string, value: any) => {
    setFilters((prev) => {
      if (type === "includeDueDiligence") {
        return { ...prev, includeDueDiligence: false };
      }

      const filtered = [...(prev as any)[type]].filter(
        (item) => item !== value,
      );
      return { ...prev, [type]: filtered };
    });
  };

  // Clear all filters
  const clearAllFilters = () => {
    setFilters({
      saleStatus: [],
      rentalStatus: [],
      renovation: [],
      includeDueDiligence: false,
    });
  };

  // Calculate totals for the filtered units
  const calculateTotals = () => {
    const totals = {
      ervRented: 0,
      ervVacant: 0,
      ervRenovation: 0,
      ervReserved: 0,
      ervSoonVacant: 0,
      actualRentalPrice: 0,
      activeProvisions: 0,
      vacant: 0,
      inRenovation: 0,
      rented: 0,
      reserved: 0,
      soonVacant: 0,
    };

    filteredUnits.forEach((unit) => {
      const erv = unit.estimatedRentalValue || 0;

      // First check if unit is in renovation
      if (unit.vacantDueToRenovation) {
        totals.inRenovation++;
        totals.ervRenovation += erv;
        return; // Skip other calculations for renovation units
      }

      // Then check contract status
      if (!unit.contracts || unit.contracts.length === 0) {
        totals.vacant++;
        totals.ervVacant += erv;
        return;
      }

      // Check for active contract
      const activeContract = unit.contracts.find(
        (contract) => contract.phase === ContractPhase.ACTIVE,
      );
      if (activeContract) {
        totals.rented++;
        totals.ervRented += erv;
        return;
      }

      // Check for termination phase contract (Aankomende leegstand)
      const terminationContract = unit.contracts.find(
        (contract) => contract.phase === ContractPhase.TERMINATION_PHASE,
      );
      if (terminationContract) {
        // Only count as upcoming vacant if not in renovation
        if (!unit.vacantDueToRenovation) {
          totals.soonVacant++;
          totals.ervSoonVacant += erv;
        }
        return;
      }

      // Check for precontractual phase
      const precontractualContract = unit.contracts.find(
        (contract) => contract.phase === ContractPhase.CONCEPT,
      );
      if (precontractualContract) {
        totals.reserved++;
        totals.ervReserved += erv;
        return;
      }

      // If no active contracts found, unit is vacant
      totals.vacant++;
      totals.ervVacant += erv;

      // Sum up monetary values
      const actualPrice = getActualRentalPrice(unit);
      if (actualPrice) totals.actualRentalPrice += actualPrice;
      const provisions = getActiveProvisions(unit);
      if (provisions) totals.activeProvisions += provisions;
    });

    return totals;
  };

  // Get active filter type and value
  const getActiveFilter = () => {
    if (filters.rentalStatus.length > 0) {
      return { type: "rentalStatus", value: filters.rentalStatus[0] };
    }
    if (filters.renovation.length > 0) {
      return { type: "renovation", value: filters.renovation[0] };
    }
    return null;
  };

  // Handle action card click
  const handleActionCardClick = (filterType: string, value: string) => {
    setFilters((prev) => {
      const newFilters = { ...prev };

      // Clear other filters first
      newFilters.rentalStatus = [];
      newFilters.renovation = [];

      // Set the appropriate filter
      switch (filterType) {
        case "rentalStatus":
          newFilters.rentalStatus = [value];
          break;
        case "renovation":
          newFilters.renovation = [value];
          break;
      }

      return newFilters;
    });
  };

  return (
    <div className="container mx-auto max-w-screen px-4 py-6">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-neutral-primary text-2xl font-bold">Units</h1>
          <p className="text-neutral-secondary-mid mt-1 text-sm">
            Toont {filteredUnits.length} / {units.length} units in eigendom en
            due diligence
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            color="primary"
            variant="solid"
            startContent={<FileArrowDown size={18} weight="bold" />}
            onClick={exportToExcel}
            className="bg-moss-primary hover:bg-moss-primary/80 border-moss-primary border-1 text-white"
          >
            Export naar Excel
          </Button>
        </div>
      </div>

      {/* Action Cards */}
      <div className="mb-4 grid grid-cols-1 gap-3 md:grid-cols-5">
        {/* Financial Cards */}
        <div
          className={`cursor-pointer rounded-lg border bg-white p-3 transition-all duration-200 ${
            getActiveFilter()?.type === "rentalStatus" &&
            getActiveFilter()?.value === "Verhuurd"
              ? "border-moss-primary bg-moss-primary/5 shadow-sm"
              : "hover:bg-neutral-primary/5"
          }`}
          onClick={() => handleActionCardClick("rentalStatus", "Verhuurd")}
        >
          <div className="mb-1 flex items-center justify-between">
            <PiggyBank size={20} className="text-moss-primary" />
            <span className="text-neutral-secondary-mid text-xs">
              {getActiveFilter() ? "Gefilterd" : "Totaal"}
            </span>
          </div>
          <div className="text-moss-primary text-xl font-bold">
            {applyMask(calculateTotals().ervRented, "valuta")}
          </div>
          <div className="text-neutral-secondary-mid text-xs">ERV verhuurd</div>
        </div>

        <div
          className={`cursor-pointer rounded-lg border bg-white p-3 transition-all duration-200 ${
            getActiveFilter()?.type === "rentalStatus" &&
            getActiveFilter()?.value === "Leegstand"
              ? "border-brick-primary bg-brick-primary/5 shadow-sm"
              : "hover:bg-neutral-primary/5"
          }`}
          onClick={() => handleActionCardClick("rentalStatus", "Leegstand")}
        >
          <div className="mb-1 flex items-center justify-between">
            <PiggyBank size={20} className="text-brick-primary" />
            <span className="text-neutral-secondary-mid text-xs">
              {getActiveFilter() ? "Gefilterd" : "Totaal"}
            </span>
          </div>
          <div className="text-brick-primary text-xl font-bold">
            {applyMask(calculateTotals().ervVacant, "valuta")}
          </div>
          <div className="text-neutral-secondary-mid text-xs">
            ERV leegstand (excl. renovatie)
          </div>
        </div>

        <div
          className={`cursor-pointer rounded-lg border bg-white p-3 transition-all duration-200 ${
            getActiveFilter()?.type === "renovation" &&
            getActiveFilter()?.value === "In Renovation"
              ? "border-brick-primary bg-brick-primary/5 shadow-sm"
              : "hover:bg-neutral-primary/5"
          }`}
          onClick={() => handleActionCardClick("renovation", "In Renovation")}
        >
          <div className="mb-1 flex items-center justify-between">
            <PiggyBank size={20} className="text-brick-primary" />
            <span className="text-neutral-secondary-mid text-xs">
              {getActiveFilter() ? "Gefilterd" : "Totaal"}
            </span>
          </div>
          <div className="text-brick-primary text-xl font-bold">
            {applyMask(calculateTotals().ervRenovation, "valuta")}
          </div>
          <div className="text-neutral-secondary-mid text-xs">
            ERV in renovatie
          </div>
        </div>

        <div
          className={`cursor-pointer rounded-lg border bg-white p-3 transition-all duration-200 ${
            getActiveFilter()?.type === "rentalStatus" &&
            getActiveFilter()?.value === "Bijna verhuurde units"
              ? "border-moss-primary bg-moss-primary/5 shadow-sm"
              : "hover:bg-neutral-primary/5"
          }`}
          onClick={() =>
            handleActionCardClick("rentalStatus", "Bijna verhuurde units")
          }
        >
          <div className="mb-1 flex items-center justify-between">
            <PiggyBank size={20} className="text-moss-primary" />
            <span className="text-neutral-secondary-mid text-xs">
              {getActiveFilter() ? "Gefilterd" : "Totaal"}
            </span>
          </div>
          <div className="text-moss-primary text-xl font-bold">
            {applyMask(calculateTotals().ervReserved, "valuta")}
          </div>
          <div className="text-neutral-secondary-mid text-xs">
            ERV bijna verhuurde units
          </div>
        </div>

        <div
          className={`cursor-pointer rounded-lg border bg-white p-3 transition-all duration-200 ${
            getActiveFilter()?.type === "rentalStatus" &&
            getActiveFilter()?.value === "Aankomende leegstand"
              ? "border-brick-primary bg-brick-primary/5 shadow-sm"
              : "hover:bg-neutral-primary/5"
          }`}
          onClick={() =>
            handleActionCardClick("rentalStatus", "Aankomende leegstand")
          }
        >
          <div className="mb-1 flex items-center justify-between">
            <PiggyBank size={20} className="text-brick-primary" />
            <span className="text-neutral-secondary-mid text-xs">
              {getActiveFilter() ? "Gefilterd" : "Totaal"}
            </span>
          </div>
          <div className="text-brick-primary text-xl font-bold">
            {applyMask(calculateTotals().ervSoonVacant, "valuta")}
          </div>
          <div className="text-neutral-secondary-mid text-xs">
            ERV aankomende leegstand
          </div>
        </div>
      </div>

      {/* Status Cards */}
      <div className="mb-4 grid grid-cols-1 gap-3 md:grid-cols-5">
        <div
          className={`cursor-pointer rounded-lg border bg-white p-3 transition-all duration-200 ${
            getActiveFilter()?.type === "rentalStatus" &&
            getActiveFilter()?.value === "Leegstand"
              ? "border-brick-primary bg-brick-primary/5 shadow-sm"
              : "hover:bg-neutral-primary/5"
          }`}
          onClick={() => handleActionCardClick("rentalStatus", "Leegstand")}
        >
          <div className="mb-1 flex items-center justify-between">
            <House size={20} className="text-brick-primary" />
            <span className="text-neutral-secondary-mid text-xs">
              {getActiveFilter() ? "Gefilterd" : "Totaal"}
            </span>
          </div>
          <div className="text-brick-primary text-xl font-bold">
            {calculateTotals().vacant}
          </div>
          <div className="text-neutral-secondary-mid text-xs">Leegstand</div>
        </div>

        <div
          className={`cursor-pointer rounded-lg border bg-white p-3 transition-all duration-200 ${
            getActiveFilter()?.type === "renovation" &&
            getActiveFilter()?.value === "In Renovation"
              ? "border-brick-primary bg-brick-primary/5 shadow-sm"
              : "hover:bg-neutral-primary/5"
          }`}
          onClick={() => handleActionCardClick("renovation", "In Renovation")}
        >
          <div className="mb-1 flex items-center justify-between">
            <Hammer size={20} className="text-brick-primary" />
            <span className="text-neutral-secondary-mid text-xs">
              {getActiveFilter() ? "Gefilterd" : "Totaal"}
            </span>
          </div>
          <div className="text-brick-primary text-xl font-bold">
            {calculateTotals().inRenovation}
          </div>
          <div className="text-neutral-secondary-mid text-xs">In renovatie</div>
        </div>

        <div
          className={`cursor-pointer rounded-lg border bg-white p-3 transition-all duration-200 ${
            getActiveFilter()?.type === "rentalStatus" &&
            getActiveFilter()?.value === "Verhuurd"
              ? "border-status-good bg-status-good/5 shadow-sm"
              : "hover:bg-neutral-primary/5"
          }`}
          onClick={() => handleActionCardClick("rentalStatus", "Verhuurd")}
        >
          <div className="mb-1 flex items-center justify-between">
            <SkipForward size={20} className="text-status-good" />
            <span className="text-neutral-secondary-mid text-xs">
              {getActiveFilter() ? "Gefilterd" : "Totaal"}
            </span>
          </div>
          <div className="text-status-good text-xl font-bold">
            {calculateTotals().rented}
          </div>
          <div className="text-neutral-secondary-mid text-xs">Verhuurd</div>
        </div>

        <div
          className={`cursor-pointer rounded-lg border bg-white p-3 transition-all duration-200 ${
            getActiveFilter()?.type === "rentalStatus" &&
            getActiveFilter()?.value === "Bijna verhuurde units"
              ? "border-moss-primary bg-moss-primary/5 shadow-sm"
              : "hover:bg-neutral-primary/5"
          }`}
          onClick={() =>
            handleActionCardClick("rentalStatus", "Bijna verhuurde units")
          }
        >
          <div className="mb-1 flex items-center justify-between">
            <Flag size={20} className="text-moss-primary" />
            <span className="text-neutral-secondary-mid text-xs">
              {getActiveFilter() ? "Gefilterd" : "Totaal"}
            </span>
          </div>
          <div className="text-moss-primary text-xl font-bold">
            {calculateTotals().reserved}
          </div>
          <div className="text-neutral-secondary-mid text-xs">
            Bijna verhuurde units
          </div>
        </div>

        <div
          className={`cursor-pointer rounded-lg border bg-white p-3 transition-all duration-200 ${
            getActiveFilter()?.type === "rentalStatus" &&
            getActiveFilter()?.value === "Aankomende leegstand"
              ? "border-brick-primary bg-brick-primary/5 shadow-sm"
              : "hover:bg-neutral-primary/5"
          }`}
          onClick={() =>
            handleActionCardClick("rentalStatus", "Aankomende leegstand")
          }
        >
          <div className="mb-1 flex items-center justify-between">
            <SkipForward size={20} className="text-brick-primary" />
            <span className="text-neutral-secondary-mid text-xs">
              {getActiveFilter() ? "Gefilterd" : "Totaal"}
            </span>
          </div>
          <div className="text-brick-primary text-xl font-bold">
            {calculateTotals().soonVacant}
          </div>
          <div className="text-neutral-secondary-mid text-xs">
            Aankomende leegstand
          </div>
        </div>
      </div>

      <div className="mb-6 flex flex-wrap items-center gap-4">
        <div className="grow">
          <Input
            placeholder="Zoek op unit ID..."
            startContent={<MagnifyingGlass size={18} />}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="max-w-md"
          />
        </div>

        <Dropdown isOpen={isDropdownOpen} onOpenChange={setIsDropdownOpen}>
          <DropdownTrigger>
            <Button
              variant="solid"
              startContent={<FadersHorizontal size={18} weight="bold" />}
              endContent={
                Object.values(filters).some(
                  (val) =>
                    (Array.isArray(val) && val.length > 0) ||
                    (val !== null && typeof val !== "boolean") ||
                    (typeof val === "boolean" && val === true),
                ) ? (
                  <Chip size="sm" color="primary" variant="flat">
                    Active
                  </Chip>
                ) : null
              }
              className="bg-neutral-primary hover:bg-neutral-primary/80 border-neutral-primary border-1 text-white"
            >
              Filters
            </Button>
          </DropdownTrigger>
          <DropdownMenu
            aria-label="Filter options"
            classNames={{ base: "min-w-[280px]" }}
            closeOnSelect={false}
          >
            <DropdownSection title="Gebouwen" showDivider>
              <DropdownItem key="due-diligence" className="py-2">
                <label className="flex w-full cursor-pointer items-center py-1">
                  <input
                    type="checkbox"
                    checked={filters.includeDueDiligence}
                    onChange={() => toggleDueDiligenceFilter()}
                    onClick={(e) => e.stopPropagation()}
                    className="accent-moss-primary mr-3 h-5 w-5 cursor-pointer"
                  />
                  <Buildings size={16} className="text-neutral-primary mr-2" />
                  <span>Toon ook panden in due diligence</span>
                </label>
              </DropdownItem>
            </DropdownSection>
            <DropdownSection title="Sale Status" showDivider>
              {Object.values(SaleStatus).map((status) => (
                <DropdownItem key={status} className="py-2">
                  <label className="flex w-full cursor-pointer items-center py-1">
                    <input
                      type="checkbox"
                      checked={filters.saleStatus.includes(status)}
                      onChange={() => toggleFilter("saleStatus", status)}
                      onClick={(e) => e.stopPropagation()}
                      className="accent-moss-primary mr-3 h-5 w-5 cursor-pointer"
                    />
                    <span>{status}</span>
                  </label>
                </DropdownItem>
              ))}
            </DropdownSection>
            <DropdownSection title="Rental Status" showDivider>
              {[
                "Leegstand",
                "Verhuurd",
                "Aankomende leegstand",
                "Bijna verhuurde units",
              ].map((status) => (
                <DropdownItem key={status} className="py-2">
                  <label className="flex w-full cursor-pointer items-center py-1">
                    <input
                      type="checkbox"
                      checked={filters.rentalStatus.includes(status)}
                      onChange={() => toggleFilter("rentalStatus", status)}
                      onClick={(e) => e.stopPropagation()}
                      className="accent-moss-primary mr-3 h-5 w-5 cursor-pointer"
                    />
                    <span>{status}</span>
                  </label>
                </DropdownItem>
              ))}
            </DropdownSection>
            <DropdownSection title="Renovation">
              {["In Renovation", "No Renovation"].map((status) => (
                <DropdownItem key={status} className="py-2">
                  <label className="flex w-full cursor-pointer items-center py-1">
                    <input
                      type="checkbox"
                      checked={filters.renovation.includes(status)}
                      onChange={() => toggleFilter("renovation", status)}
                      onClick={(e) => e.stopPropagation()}
                      className="accent-moss-primary mr-3 h-5 w-5 cursor-pointer"
                    />
                    <span>{status}</span>
                  </label>
                </DropdownItem>
              ))}
            </DropdownSection>
          </DropdownMenu>
        </Dropdown>
      </div>

      {/* Active filters row */}
      {getActiveFilters().length > 0 && (
        <div className="bg-neutral-primary/5 border-neutral-primary/10 mb-4 flex flex-wrap items-center gap-2 rounded-lg border p-3">
          <span className="text-neutral-primary mr-1 text-sm font-medium">
            Actieve filters:
          </span>
          {getActiveFilters().map((filter, index) => (
            <Chip
              key={`${filter.type}-${filter.value}-${index}`}
              onClose={() => removeFilter(filter.type, filter.value)}
              variant="flat"
              color="primary"
              className="bg-brick-primary/10 text-brick-primary border-brick-primary/20 border"
              endContent={
                <X size={14} weight="bold" className="text-brick-primary" />
              }
              classNames={{
                closeButton: "text-brick-primary hover:bg-brick-primary/20",
                content: "text-brick-primary font-medium",
              }}
            >
              {filter.label}
            </Chip>
          ))}
          {getActiveFilters().length > 1 && (
            <Button
              size="sm"
              variant="flat"
              className="bg-neutral-primary hover:bg-neutral-primary/80 ml-2 text-white"
              onClick={clearAllFilters}
              startContent={<X size={14} weight="bold" />}
            >
              Verwijder alles
            </Button>
          )}
        </div>
      )}

      <div className="overflow-x-auto rounded-lg bg-white shadow-sm">
        {isLoading ? (
          <div className="flex h-64 items-center justify-center">
            <Spinner
              label="Gegevens aan het ophalen"
              color="success"
              size="lg"
            />
          </div>
        ) : (
          <div ref={containerRef}>
            <Table
              aria-label="Units table"
              classNames={{
                wrapper: "shadow-none rounded-lg",
                thead: "border-b-2 border-neutral-primary/40",
                th: "rounded-none! bg-white py-3 text-sm text-neutral-primary font-medium cursor-pointer",
                tbody: "divide-y divide-neutral-primary/15",
                tr: "hover:bg-neutral-primary/5",
                td: "py-3",
                table: "min-w-full",
              }}
            >
              <TableHeader>
                <TableColumn onClick={() => requestSort("id")}>
                  <div className="flex items-center gap-1">
                    <House size={16} weight="duotone" />
                    Unit ID {renderSortIcon("id")}
                  </div>
                </TableColumn>
                <TableColumn>
                  <div className="flex items-center gap-1">
                    <Buildings size={16} weight="duotone" />
                    Gebouw ID
                  </div>
                </TableColumn>
                <TableColumn>
                  <div className="flex items-center gap-1">
                    <Buildings size={16} weight="duotone" />
                    Vennootschap
                  </div>
                </TableColumn>
                <TableColumn>
                  <div className="flex items-center gap-1">
                    <Building size={16} weight="duotone" />
                    Type
                  </div>
                </TableColumn>
                <TableColumn>
                  <div className="flex items-center gap-1">
                    <House size={16} weight="duotone" />
                    Subtype
                  </div>
                </TableColumn>
                <TableColumn onClick={() => requestSort("surface")}>
                  <div className="flex items-center gap-1">
                    <Building size={16} weight="duotone" />
                    Surface {renderSortIcon("surface")}
                  </div>
                </TableColumn>
                <TableColumn onClick={() => requestSort("outsideSpaceSurface")}>
                  <div className="flex items-center gap-1">
                    <TreePalm size={16} weight="duotone" />
                    Terras / buitenruimte (m²){" "}
                    {renderSortIcon("outsideSpaceSurface")}
                  </div>
                </TableColumn>
                <TableColumn>
                  <div className="flex items-center gap-1">
                    <Bed size={16} weight="duotone" />
                    Slaapkamers
                  </div>
                </TableColumn>
                <TableColumn>
                  <div className="flex items-center gap-1">
                    <Lightning size={16} weight="duotone" />
                    EPC
                  </div>
                </TableColumn>
                <TableColumn>
                  <div className="flex items-center gap-1">
                    <Flag size={16} weight="duotone" />
                    Sale Status
                  </div>
                </TableColumn>
                <TableColumn>
                  <div className="flex items-center gap-1">
                    <SkipForward size={16} weight="duotone" />
                    Rental Status
                  </div>
                </TableColumn>
                <TableColumn onClick={() => requestSort("contractStartDate")}>
                  <div className="flex items-center gap-1">
                    <CalendarBlank size={16} weight="duotone" />
                    Startdatum huurcontract{" "}
                    {renderSortIcon("contractStartDate")}
                  </div>
                </TableColumn>
                <TableColumn>
                  <div className="flex items-center gap-1">
                    <Hammer size={16} weight="duotone" />
                    Renovation
                  </div>
                </TableColumn>
                <TableColumn onClick={() => requestSort("price")}>
                  <div className="flex items-center gap-1">
                    <PiggyBank size={16} weight="duotone" />
                    ERV {renderSortIcon("price")}
                  </div>
                </TableColumn>
                <TableColumn onClick={() => requestSort("actualRentalPrice")}>
                  <div className="flex items-center gap-1">
                    <PiggyBank size={16} weight="duotone" />
                    Actuele huurprijs {renderSortIcon("actualRentalPrice")}
                  </div>
                </TableColumn>
                <TableColumn onClick={() => requestSort("parkingPrice")}>
                  <div className="flex items-center gap-1">
                    <PiggyBank size={16} weight="duotone" />
                    Parking huurprijs {renderSortIcon("parkingPrice")}
                  </div>
                </TableColumn>
                <TableColumn>
                  <div className="flex items-center gap-1">
                    <PiggyBank size={16} weight="duotone" />
                    Actuele provisies
                  </div>
                </TableColumn>
                <TableColumn>
                  <div className="flex items-center gap-1">
                    <Image size={16} weight="duotone" />
                    Image
                  </div>
                </TableColumn>
                <TableColumn>Actions</TableColumn>
              </TableHeader>
              <TableBody emptyContent={"No units found"}>
                {Array.isArray(filteredUnits) ? (
                  filteredUnits.map((unit) => {
                    const rentalStatus = getRentalStatus(unit);
                    const actualPrice = getActualRentalPrice(unit);
                    const parkingPrice = getParkingRentalPrice(unit);
                    const activeProvisions = getActiveProvisions(unit);
                    const priceDifference = calculatePriceDifference(unit);
                    const contractStartDate = unit.contractStartDate
                      ? formatContractStartDate(unit.contractStartDate)
                      : null;
                    const unitId = unit.id as string;
                    const isVisible = visibleUnits.has(unitId);

                    return (
                      <TableRow
                        key={unitId}
                        onClick={() => handleUnitClick(unitId)}
                        className="cursor-pointer"
                      >
                        <TableCell>
                          <Chip
                            className="gap-1"
                            classNames={{
                              base: "h-fit bg-white px-2 border border-brick-primary text-brick-primary",
                              content: "px-0 font-medium",
                            }}
                          >
                            {unit.id}{" "}
                            {unit.referencePlus
                              ? `(${unit.referencePlus})`
                              : ""}
                          </Chip>
                        </TableCell>
                        <TableCell>
                          <span className="text-neutral-secondary-mid text-sm">
                            {unit.building?.id || "-"}
                          </span>
                        </TableCell>
                        <TableCell>
                          <span className="text-neutral-secondary-mid text-sm">
                            {unit.building?.company?.name || "-"}
                          </span>
                        </TableCell>
                        <TableCell>
                          <Chip color="default" variant="flat" size="sm">
                            {unit.unitType ||
                              unit.unitSubtype ||
                              "Niet gespecificeerd"}
                          </Chip>
                        </TableCell>
                        <TableCell>
                          {unit.unitSubtype ? (
                            <Chip color="default" variant="flat" size="sm">
                              {unit.unitSubtype}
                            </Chip>
                          ) : (
                            <span className="text-neutral-secondary-mid text-sm">
                              -
                            </span>
                          )}
                        </TableCell>
                        <TableCell>
                          {/*
                            `??` (not `||`): a net surface of 0 is a real value
                            and must not fall through to the gross surface, and
                            a missing surface renders "-" instead of the
                            "undefined m²" that masking a nullish value gives.
                          */}
                          {(unit.netSurface ?? unit.grossSurface ?? null) ===
                          null
                            ? "-"
                            : applyMask(
                                unit.netSurface ?? unit.grossSurface,
                                "surface",
                              )}
                        </TableCell>
                        <TableCell>
                          <EditableNumberCell
                            value={unit.outsideSpaceSurface}
                            mask="surface"
                            ariaLabel={`Oppervlakte buitenruimte unit ${unitId}`}
                            onSave={(value) =>
                              saveOutsideSpaceSurface(unitId, value)
                            }
                            errorContext={{ unitId, screen: "units-list" }}
                          />
                        </TableCell>
                        <TableCell>
                          {unit.unitInspectionInfo?.numBedrooms ? (
                            <span className="text-sm">
                              {unit.unitInspectionInfo.numBedrooms}
                            </span>
                          ) : (
                            <span className="text-neutral-secondary-mid text-sm">
                              -
                            </span>
                          )}
                        </TableCell>
                        <TableCell>{renderUnitEpc(unit)}</TableCell>
                        <TableCell>
                          <Chip
                            color={
                              unit.saleStatus === SaleStatus.ForSale
                                ? "primary"
                                : unit.saleStatus === SaleStatus.Sold
                                  ? "success"
                                  : "default"
                            }
                            variant="flat"
                            size="sm"
                          >
                            {unit.saleStatus || "Not specified"}
                          </Chip>
                        </TableCell>
                        <TableCell>
                          <Chip
                            color={getStatusChipColor(rentalStatus)}
                            variant="flat"
                            size="sm"
                          >
                            {rentalStatus}
                          </Chip>
                        </TableCell>
                        <TableCell>
                          {contractStartDate ? (
                            <span className="text-sm">{contractStartDate}</span>
                          ) : (
                            <span className="text-neutral-secondary-mid text-sm">
                              -
                            </span>
                          )}
                        </TableCell>
                        <TableCell>
                          <Chip
                            color={
                              unit.vacantDueToRenovation ? "warning" : "success"
                            }
                            variant="flat"
                            size="sm"
                          >
                            {unit.vacantDueToRenovation
                              ? "In Renovation"
                              : "No Renovation"}
                          </Chip>
                        </TableCell>
                        <TableCell>
                          {applyMask(unit.estimatedRentalValue, "valuta")}
                        </TableCell>
                        <TableCell>
                          {actualPrice ? (
                            <div className="flex items-center gap-2">
                              <span>{applyMask(actualPrice, "valuta")}</span>
                              {priceDifference !== null && (
                                <Tooltip
                                  content={`${Math.abs(priceDifference).toFixed(1)}% ${priceDifference >= 0 ? "hoger" : "lager"} dan ERV`}
                                  placement="top"
                                >
                                  {priceDifference >= 0 ? (
                                    <CaretUp
                                      size={16}
                                      weight="fill"
                                      className="text-success cursor-help"
                                    />
                                  ) : (
                                    <CaretDown
                                      size={16}
                                      weight="fill"
                                      className="text-danger cursor-help"
                                    />
                                  )}
                                </Tooltip>
                              )}
                            </div>
                          ) : (
                            "-"
                          )}
                        </TableCell>
                        <TableCell>
                          {parkingPrice !== null
                            ? applyMask(parkingPrice, "valuta")
                            : "-"}
                        </TableCell>
                        <TableCell>
                          {activeProvisions
                            ? applyMask(activeProvisions, "valuta")
                            : "-"}
                        </TableCell>
                        <TableCell>
                          <div
                            className="border-neutral-primary/10 h-16 w-24 overflow-hidden rounded-md border"
                            onClick={(e) => e.stopPropagation()}
                            data-unit-id={unitId}
                          >
                            {isVisible ? (
                              <ImagesContainer
                                pageSize={1}
                                canUpload={false}
                                canOpenImage={true}
                                variant="light"
                                display="inline"
                                fileLabel={FileLabel.CoverImage}
                                unitId={unitId}
                              />
                            ) : (
                              <div className="flex h-full w-full items-center justify-center bg-neutral-100">
                                <Image
                                  size={24}
                                  weight="duotone"
                                  className="text-neutral-300"
                                />
                              </div>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Button
                            isIconOnly
                            size="sm"
                            variant="light"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleUnitClick(unitId);
                            }}
                          >
                            <Eye size={18} />
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })
                ) : (
                  <TableRow>
                    <TableCell colSpan={19}>Error loading units</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        )}
      </div>
    </div>
  );
};

export default Units;
